import { Injectable } from '@angular/core';
import {
  CacheEntry,
  AnalysisSectionType,
  DEFAULT_CACHE_TTL,
} from '../models/analysis-store.model';
import { environment } from '@env/environment';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

/**
 * Unified cache service for all analysis store sections.
 *
 * Responsibilities:
 * - Manage TTL-based caching per section
 * - Validate cache entries (TTL, etag)
 * - Prune expired entries
 * - Provide consistent cache interface
 */
@Injectable({ providedIn: 'root' })
export class AnalysisCacheService {
  // Separate cache maps per section
  private detailCache = new Map<string, CacheEntry>();
  private filesCache = new Map<string, CacheEntry>();
  private polygonsCache = new Map<string, CacheEntry>();
  private planningsCache = new Map<string, CacheEntry>();

  constructor() {
    // Prune expired entries every 10 minutes
    setInterval(() => this.pruneExpiredEntries(), 10 * 60 * 1000);
  }

  /**
   * Get cached data for a section, or null if missing/expired
   */
  getOrNull<T>(
    section: AnalysisSectionType,
    cacheKey: string,
  ): T | null {
    const cache = this.getCacheForSection(section);
    const entry = cache.get(cacheKey);

    if (!entry) {
      debugLog(
        `[AnalysisCacheService] Cache miss for ${section}:${cacheKey}`,
      );
      return null;
    }

    if (this.isExpired(entry)) {
      debugLog(
        `[AnalysisCacheService] Cache expired for ${section}:${cacheKey}, TTL: ${entry.ttl}ms`,
      );
      cache.delete(cacheKey);
      return null;
    }

    debugLog(`[AnalysisCacheService] Cache hit for ${section}:${cacheKey}`);
    return entry.data as T;
  }

  /**
   * Set cached data for a section
   */
  set<T>(
    section: AnalysisSectionType,
    cacheKey: string,
    data: T,
    ttl: number = DEFAULT_CACHE_TTL,
  ): void {
    const cache = this.getCacheForSection(section);
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    debugLog(
      `[AnalysisCacheService] Cached ${section}:${cacheKey}, TTL: ${ttl}ms`,
    );
  }

  /**
   * Invalidate all cache entries for a section
   */
  invalidate(section: AnalysisSectionType): void {
    const cache = this.getCacheForSection(section);
    const size = cache.size;
    cache.clear();
    debugLog(`[AnalysisCacheService] Invalidated ${section} (${size} entries)`);
  }

  /**
   * Invalidate multiple sections
   */
  invalidateMultiple(sections: AnalysisSectionType[]): void {
    sections.forEach((section) => this.invalidate(section));
  }

  /**
   * Clear all caches
   */
  invalidateAll(): void {
    this.detailCache.clear();
    this.filesCache.clear();
    this.polygonsCache.clear();
    this.planningsCache.clear();
    debugLog(`[AnalysisCacheService] Cleared all caches`);
  }

  /**
   * Prune expired entries to prevent memory accumulation
   */
  pruneExpiredEntries(): void {
    const prune = (cache: Map<string, CacheEntry>, name: string) => {
      let pruned = 0;
      for (const [key, entry] of cache.entries()) {
        if (this.isExpired(entry)) {
          cache.delete(key);
          pruned++;
        }
      }
      if (pruned > 0) {
        debugLog(
          `[AnalysisCacheService] Pruned ${pruned} expired entries from ${name}`,
        );
      }
    };

    prune(this.detailCache, 'detail');
    prune(this.filesCache, 'files');
    prune(this.polygonsCache, 'polygons');
    prune(this.planningsCache, 'plannings');
  }

  /**
   * Get cache statistics for debugging
   */
  getStats(): Record<string, number> {
    return {
      detail: this.detailCache.size,
      files: this.filesCache.size,
      polygons: this.polygonsCache.size,
      plannings: this.planningsCache.size,
    };
  }

  // ─── Private helpers ───────────────────────────────────────────────────

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private getCacheForSection(
    section: AnalysisSectionType,
  ): Map<string, CacheEntry> {
    switch (section) {
      case 'detail':
        return this.detailCache;
      case 'files':
        return this.filesCache;
      case 'polygons':
        return this.polygonsCache;
      case 'plannings':
        return this.planningsCache;
      default:
        throw new Error(`Unknown cache section: ${section}`);
    }
  }
}
